/**
 * OrderOffersPage — صفحة عروض المزودين
 * Redesigned with adaptive water theme (light/dark)
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import SAR from '../../../components/SAR';

const T = (v) => v;
const Money = ({ n }) =>
  n != null ? <><span dir="ltr">{parseFloat(n).toFixed(2)}</span>{' '}<SAR className="h-[0.85em] w-auto inline" /></> : <>—</>;

const fmtMin = m => m == null ? null : m < 60 ? `${Math.round(m)} د` : `${(m/60).toFixed(1)} س`;
const fmtKm  = k => k != null ? `${parseFloat(k).toFixed(1)} كم` : null;

function BidCard({ bid, onSelect, selecting, rank }) {
  const { badges } = bid;
  const isBest     = badges?.is_best;
  const isCheapest = badges?.is_cheapest && !isBest;
  const isFastest  = badges?.is_fastest  && !isBest;
  const isSelecting = selecting === bid.bid_id;

  return (
    <div
      className="wt-anim-fadeUp"
      style={{
        position: 'relative',
        background: isBest ? 'linear-gradient(135deg, rgba(5,150,105,0.1), rgba(16,185,129,0.06))' : 'var(--wt-surface)',
        border: isBest ? '1.5px solid rgba(16,185,129,0.4)' : '1px solid var(--wt-border)',
        borderRadius: '1.25rem',
        padding: '1.125rem',
        marginBottom: '0.75rem',
        animationDelay: `${rank * 0.08}s`,
        boxShadow: isBest ? '0 0 24px rgba(16,185,129,0.15)' : 'none',
        overflow: 'hidden',
      }}
    >
      {/* Best banner */}
      {isBest && (
        <div style={{
          position: 'absolute', top: 0, right: 0,
          background: 'linear-gradient(135deg, #059669, #10b981)',
          color: '#fff', fontSize: '0.65rem', fontWeight: 800,
          padding: '0.18rem 0.75rem', borderRadius: '0 1.25rem 0 0.75rem',
          letterSpacing: '0.03em',
        }}>★ الأفضل</div>
      )}

      {/* Badges */}
      <div style={{ display: 'flex', gap: '0.3rem', flexWrap: 'wrap', marginBottom: '0.75rem', paddingTop: isBest ? '0.5rem' : 0 }}>
        {isCheapest && <Chip label="الأرخص" color="emerald" />}
        {isFastest  && <Chip label="الأسرع"  color="sky" />}
        {bid.ship_rating && <Chip label={`★ ${parseFloat(bid.ship_rating).toFixed(1)}`} color="amber" />}
      </div>

      {/* Main row */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.875rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', flex: 1, minWidth: 0 }}>
          <div style={{
            width: 42, height: 42, borderRadius: '0.875rem', flexShrink: 0,
            background: 'linear-gradient(135deg, rgba(10,186,181,0.15), rgba(0,102,204,0.1))',
            border: '1px solid rgba(10,186,181,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <svg style={{ width: 20, height: 20, color: '#0ABAB5' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
              <path strokeLinecap="round" d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4z"/>
            </svg>
          </div>
          <div style={{ minWidth: 0 }}>
            <p style={{ color: 'var(--wt-text-primary)', fontWeight: 800, fontSize: '0.9rem', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {bid.ship_name || 'مزود'}
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.2rem', flexWrap: 'wrap' }}>
              {bid.driver_name && <span style={{ color: 'var(--wt-text-muted)', fontSize: '0.72rem' }}>{bid.driver_name}</span>}
              {bid.eta_minutes != null && (
                <span style={{ color: 'var(--wt-text-muted)', fontSize: '0.72rem', display: 'flex', alignItems: 'center', gap: '0.2rem' }}>
                  <svg style={{ width: 11, height: 11 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><circle cx="12" cy="12" r="9"/><path strokeLinecap="round" d="M12 7v5l3 3"/></svg>
                  {fmtMin(bid.eta_minutes)}
                </span>
              )}
              {fmtKm(bid.distance_km) && (
                <span style={{ color: 'var(--wt-text-muted)', fontSize: '0.72rem' }}>· {fmtKm(bid.distance_km)}</span>
              )}
            </div>
          </div>
        </div>

        {/* Price */}
        <div style={{ textAlign: 'left', flexShrink: 0 }}>
          <p style={{ color: '#10b981', fontWeight: 900, fontSize: '1.4rem', margin: 0, lineHeight: 1 }}>
            <Money n={bid.total_amount} />
          </p>
          <p style={{ color: 'var(--wt-text-muted)', fontSize: '0.65rem', margin: '0.15rem 0 0', textAlign: 'center' }}>الإجمالي</p>
        </div>
      </div>

      {/* Price breakdown */}
      <div style={{
        background: 'var(--wt-bg-2)',
        border: '1px solid var(--wt-border)',
        borderRadius: '0.875rem',
        padding: '0.625rem 0.875rem',
        display: 'grid', gridTemplateColumns: '1fr auto',
        gap: '0.25rem 0.875rem', marginBottom: '0.875rem',
        fontSize: '0.78rem',
      }}>
        {[
          ['سعر الطن', bid.price_per_ton],
          ['المجموع', bid.subtotal],
          bid.delivery_fee > 0 && ['التوصيل', bid.delivery_fee],
          ['الضريبة 15%', bid.tax_total],
        ].filter(Boolean).map(([label, val]) => (
          <React.Fragment key={label}>
            <span style={{ color: 'var(--wt-text-muted)' }}>{label}</span>
            <span style={{ color: 'var(--wt-text-secondary)', fontWeight: 700, textAlign: 'left', direction: 'ltr' }}>
              {val != null ? parseFloat(val).toFixed(2) : '—'}
            </span>
          </React.Fragment>
        ))}
      </div>

      {/* Select Button */}
      <button
        disabled={isSelecting}
        onClick={() => onSelect(bid.bid_id)}
        style={{
          width: '100%', padding: '0.825rem',
          borderRadius: '0.875rem',
          border: 'none', cursor: isSelecting ? 'default' : 'pointer',
          fontWeight: 800, fontSize: '0.9rem',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
          opacity: isSelecting ? 0.7 : 1,
          background: isBest
            ? 'linear-gradient(135deg, #059669, #10b981)'
            : 'linear-gradient(135deg, #0ABAB5, #0066CC)',
          color: '#fff',
          boxShadow: isBest ? '0 4px 16px rgba(16,185,129,0.3)' : '0 4px 16px rgba(10,186,181,0.3)',
          fontFamily: "'Cairo', sans-serif",
          transition: 'opacity 0.2s',
        }}
      >
        {isSelecting ? (
          <>
            <span style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', borderRadius: '50%', animation: 'wt-spin 0.6s linear infinite', display: 'inline-block' }} />
            جارٍ التأكيد...
          </>
        ) : (
          <>
            <svg style={{ width: 16, height: 16 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" d="M4.5 12.75l6 6 9-13.5"/></svg>
            اختيار هذا العرض
          </>
        )}
      </button>
    </div>
  );
}

function Chip({ label, color }) {
  const map = {
    emerald: { bg: 'rgba(16,185,129,0.12)', border: 'rgba(16,185,129,0.28)', color: 'var(--wt-emerald)' },
    sky:     { bg: 'rgba(14,165,233,0.12)', border: 'rgba(14,165,233,0.28)', color: 'var(--wt-cyan-bright)' },
    amber:   { bg: 'rgba(245,158,11,0.12)', border: 'rgba(245,158,11,0.25)', color: 'var(--wt-amber)' },
  };
  const s = map[color] || map.sky;
  return (
    <span style={{
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
      fontSize: '0.68rem', fontWeight: 800, borderRadius: 999, padding: '0.15rem 0.6rem',
      fontFamily: "'Cairo', sans-serif",
    }}>{label}</span>
  );
}

export default function OrderOffersPage() {
  const { id: orderId } = useParams();
  const { apiCall }     = useCustomerAuth();
  const nav             = useNavigate();

  const [bids,      setBids]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selecting, setSelecting] = useState(null);
  const [error,     setError]     = useState(null);
  const [notice,    setNotice]    = useState(null);

  const showNotice = (msg, type = 'success') => {
    setNotice({ msg, type });
    setTimeout(() => setNotice(null), 4000);
  };

  const loadBids = useCallback(async () => {
    try {
      const data = await apiCall(`/customer/marketplace/orders/${orderId}/bids`);
      setBids(data.bids || []);
    } catch (e) {
      setError(e.message || 'تعذّر تحميل العروض');
    } finally {
      setLoading(false);
    }
  }, [apiCall, orderId]);

  useEffect(() => {
    loadBids();
    const iv = setInterval(loadBids, 15000);
    return () => clearInterval(iv);
  }, [loadBids]);

  const handleSelect = async (bidId) => {
    setSelecting(bidId);
    try {
      await apiCall(`/customer/marketplace/orders/${orderId}/select-bid`, {
        method: 'POST', body: JSON.stringify({ bid_id: bidId }),
      });
      showNotice('تم تأكيد طلبك! السائق في الطريق إليك');
      setTimeout(() => nav(`/customer/orders/${orderId}`), 1600);
    } catch (e) {
      showNotice(e.message || 'فشل تأكيد العرض — حاول مجدداً', 'error');
      setSelecting(null);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

      {/* ── Toast notice ── */}
      {notice && (
        <div style={{
          position: 'fixed', top: 72, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999,
          background: notice.type === 'error'
            ? 'linear-gradient(135deg, #9f1239, #e11d48)'
            : 'linear-gradient(135deg, #065f46, #059669)',
          color: '#fff', borderRadius: '1rem', padding: '0.75rem 1.5rem',
          fontWeight: 700, fontSize: '0.875rem',
          boxShadow: '0 8px 32px rgba(0,0,0,0.35)',
          fontFamily: "'Cairo', sans-serif",
          whiteSpace: 'nowrap',
          animation: 'wt-dropIn 0.3s both',
        }}>
          {notice.msg}
        </div>
      )}

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
        <button onClick={() => nav(-1)} style={{
          width: 38, height: 38, borderRadius: '0.75rem', flexShrink: 0,
          background: 'var(--wt-surface-2)', border: '1px solid var(--wt-border)',
          color: 'var(--wt-text-2)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg style={{ width: 18, height: 18 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M15 19l-7-7 7-7"/></svg>
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ color: 'var(--wt-text)', fontSize: '1.15rem', fontWeight: 900, margin: 0 }}>عروض المزودين</h1>
          <p style={{ color: 'var(--wt-text-3)', fontSize: '0.75rem', margin: 0 }}>اختر العرض الأنسب لك</p>
        </div>
        <button onClick={loadBids} style={{
          display: 'flex', alignItems: 'center', gap: '0.3rem',
          background: 'var(--wt-surface-2)', border: '1px solid var(--wt-border)',
          borderRadius: '0.75rem', padding: '0.45rem 0.875rem',
          color: 'var(--wt-text-2)', fontSize: '0.78rem', fontWeight: 700,
          cursor: 'pointer', fontFamily: "'Cairo', sans-serif",
        }}>
          <svg style={{ width: 13, height: 13 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" d="M4 4v5h5M20 20v-5h-5M4 9a9 9 0 0114.65-4.65M20 15a9 9 0 01-14.65 4.65"/></svg>
          تحديث
        </button>
      </div>

      {/* ── Loading ── */}
      {loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {[1,2,3].map(i => <div key={i} className="wt-skeleton" style={{ height: 200, borderRadius: 'var(--wt-radius-md)' }} />)}
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="wt-glass" style={{ padding: '1rem', borderRadius: 'var(--wt-radius-md)', borderColor: 'rgba(244,63,94,0.3)' }}>
          <p style={{ color: '#fda4af', fontWeight: 700, margin: 0 }}>{error}</p>
        </div>
      )}

      {/* ── Empty / Waiting ── */}
      {!loading && !error && bids.length === 0 && (
        <div className="wt-glass" style={{ padding: '3rem 1.5rem', textAlign: 'center', borderRadius: 'var(--wt-radius-lg)' }}>
          {/* Ripple animation */}
          <div style={{ position: 'relative', width: 72, height: 72, margin: '0 auto 1.25rem' }}>
            <div className="wt-ripple" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--wt-cyan-pale)' }} />
            <div className="wt-ripple wt-ripple-2" style={{ position: 'absolute', inset: 0, borderRadius: '50%', background: 'var(--wt-cyan-pale)' }} />
            <div style={{
              position: 'relative', width: '100%', height: '100%', borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--wt-cyan-pale), var(--wt-teal-pale))',
              border: '1.5px solid var(--wt-border-hover)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <svg style={{ width: 30, height: 30, color: 'var(--wt-cyan-bright)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8}>
                <circle cx="12" cy="12" r="9"/><path strokeLinecap="round" d="M12 7v5l3 3"/>
              </svg>
            </div>
          </div>
          <h3 style={{ color: 'var(--wt-text)', fontWeight: 800, fontSize: '1rem', margin: '0 0 0.5rem' }}>في انتظار العروض</h3>
          <p style={{ color: 'var(--wt-text-3)', fontSize: '0.825rem', margin: '0 0 0.75rem', lineHeight: 1.6, maxWidth: 280, marginLeft: 'auto', marginRight: 'auto' }}>
            تم إرسال طلبك — ستظهر العروض هنا فور وصولها
          </p>
          <p style={{ color: 'var(--wt-text-3)', fontSize: '0.7rem', opacity: 0.6 }}>تحديث تلقائي كل 15 ثانية</p>
        </div>
      )}

      {/* ── Bids list ── */}
      {!loading && bids.length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p style={{ color: 'var(--wt-text-2)', fontSize: '0.8rem', fontWeight: 700, margin: 0 }}>
              {bids.length} {bids.length === 1 ? 'عرض متاح' : 'عروض متاحة'}
            </p>
            <p style={{ color: 'var(--wt-text-3)', fontSize: '0.72rem', margin: 0 }}>مرتبة: الأفضل أولاً</p>
          </div>
          {bids.map((bid, i) => (
            <BidCard key={bid.bid_id} bid={bid} onSelect={handleSelect} selecting={selecting} rank={i} />
          ))}
        </>
      )}

      <style>{`
        @keyframes wt-spin { to { transform: rotate(360deg); } }
        @keyframes wt-ripple { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.2); opacity: 0; } }
        .wt-ripple { animation: wt-ripple 2s ease-out infinite; }
        .wt-ripple-2 { animation-delay: 0.8s; }
      `}</style>
    </div>
  );
}
